<template>
  <div
    v-if="!isMarketWithoutSale && product.available"
    :class="bemBlock"
    :data-context="context"
    :data-step="step"
  >
    <input type="hidden" class="add-to-cart-url" :value="addToCartUrl" />
    <button
      :class="[
        bemElement('add'),
        'icon-check',
        'icon-check--plus',
        { 'icon-check--checked': step === 1 || step === 2 },
      ]"
      :data-attr-url="hasUniqueSelectableSize() ? sizes[0].url : ''"
      :data-step="step"
      @click="handleAddButtonClick"
    >
      <span :class="[bemElement('add-text'), 'visually-hidden']"
        >Size & Add</span
      >
    </button>
    <div :class="bemElement('content')">
      <BaseProductTileSizeSelector
        v-show="!hasUniqueSelectableSize()"
        bem="quick-shop-sizes"
        :class="[bemElement('sizes'), 'u-scrollbar-byl']"
        :context="context"
        :product="product"
        :selected-size="currentSize"
        :show-in-modal="showInModal"
        @close="closeModal"
        @size:change="selectSize"
      />
      <p v-if="showAddedMessage && !showInModal" :class="bemElement('added')">
        {{ addedMessage }}
      </p>
      <p
        v-if="step === steps.REMOVED_FROM_CART && shouldShowCartMessages"
        :class="bemElement('removed')"
      >
        {{ $msg('quickShop.removedFromBag', 'productTile') }}
      </p>
    </div>
    <div
      v-if="showAddedMessage && !showInModal"
      :class="bemElement('checkout-link-container')"
    >
      <a :class="bemElement('checkout-link')" :href="checkoutUrl"
        >{{ proceedToCheckoutMessage.beginning }}
        <span class="u-nowrap"
          >{{ proceedToCheckoutMessage.lastWord
          }}<i
            :class="bemElement('checkout-link-icon')"
            class="icon-arrow-filled" /></span
      ></a>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line
///<reference path="../../../../../jsdoc-types.js" />

import { mapGetters } from 'vuex';
import bemClassesMixin from '../mixins/bemClassesMixin';
import productPropBaseMixin from '../mixins/productPropBaseMixin';
import productPropSizesMixin from '../mixins/productPropSizesMixin';
import BaseProductTileSizeSelector from './BaseProductTileSizeSelector.vue';
import { getOriginalPID } from '../../common/utils/doubleLookUtils';
import { getters } from '../store';
import EventBusConfig from '../config/eventBusConfig';
import { ITEMS } from '../config/modalSizeSelectorConfig';
import { getVariationGroupId } from '../../common/helpers/productIdHelpers';
import getFrontConfig from '../../common/getFrontConfig';
import windowResizeMixin from '../mixins/windowResizeMixin';

const {
  vue: {
    BaseProductTile: {
      context: { plpGrid },
    },
  },
  responsive: { bp_768: bp768 },
  urls: { addToCart, checkout },
  isActiveModalSizeSelector,
  isMarketWithoutSale,
} = getFrontConfig();

const STEPS = {
  INIT: 0,
  SIZE_SELECTION: 1,
  ADDED_TO_CART: 2,
  REMOVED_FROM_CART: 3,
};

export default {
  name: 'BaseProductTileQuickShop',

  components: {
    BaseProductTileSizeSelector,
  },

  mixins: [
    bemClassesMixin,
    productPropBaseMixin,
    productPropSizesMixin,
    windowResizeMixin,
  ],

  props: {
    context: {
      type: String,
      required: false,
      default: plpGrid,
    },
  },

  data: () => ({
    addToCartUrl: undefined,
    checkoutUrl: undefined,
    dataChangeBreakpoint: bp768,
    shouldShowCartMessages: true,
    initializedFromCart: false,
    isMarketWithoutSale: undefined,
    currentSize: {},
    step: STEPS.INIT,
    steps: STEPS,
    willBeAddedToCart: false,
    willBeRemovedFromCart: false,
  }),

  computed: {
    ...mapGetters({
      cartIncludes: getters.cart.includes,
    }),
    isInCart() {
      return this.cartIncludes(this.productId);
    },
    addedMessage() {
      const lastSelectedSize = this.getLastSelectedSize()?.displayValue;
      let msg = '';

      if (lastSelectedSize === 'UN' || !lastSelectedSize) {
        msg = this.$msg('text.alert.addedtobasket.modal', 'product');
      } else {
        msg = this.$msg('button.sizeaddedmobile', 'product', [
          lastSelectedSize,
        ]);
      }

      return msg;
    },
    /**
     * Return msg splitted in last word in order to avoid the wrapping of the
     * icon alone
     */
    proceedToCheckoutMessage() {
      const msg = this.$msg('quickShop.proceedToCheckout', 'productTile');
      if (!msg) {
        return {
          beginning: '',
          lastWord: '',
        };
      }
      const words = msg.split(' ');
      if (words.length === 1) {
        return {
          beginning: '',
          lastWord: msg,
        };
      }
      return {
        beginning: words.slice(0, words.length - 1).join(' '),
        lastWord: words[words.length - 1],
      };
    },
    showAddedMessage() {
      return (
        this.step === STEPS.ADDED_TO_CART &&
        !this.initializedFromCart &&
        this.shouldShowCartMessages
      );
    },
    showInModal() {
      const isMultiSizeSelection =
        STEPS.SIZE_SELECTION === this.step && !this.hasUniqueSize();
      const isValidContentToShow =
        isMultiSizeSelection || this.showAddedMessage;
      return (
        isActiveModalSizeSelector &&
        this.isBreakpointMobile &&
        isValidContentToShow
      );
    },
  },

  watch: {
    isInCart(newVal) {
      this.shouldShowCartMessages = false;
      if (newVal) {
        this.step = STEPS.ADDED_TO_CART;
      } else {
        this.handleAfterRemoveSizeFromCart(this.productId);
      }
    },
    step(val) {
      if (val === STEPS.REMOVED_FROM_CART) {
        // Product removed, back to first step
        setTimeout(() => {
          this.willBeRemovedFromCart = false;
          this.step = STEPS.INIT;
        }, 2300);
      }
      if (val === STEPS.INIT) {
        this.$emit('close');
      } else {
        this.$emit('open');
      }
    },
  },

  created() {
    this.addToCartUrl = addToCart;
    this.checkoutUrl = checkout;
    this.isMarketWithoutSale = isMarketWithoutSale;
  },

  mounted() {
    if (this.$store) {
      // find id in store cart
      const exists = this.cartIncludes(this.productId);
      if (exists) {
        // this will show the active bullet
        this.initializedFromCart = true;
        this.step = STEPS.ADDED_TO_CART;
      }
    }
    this.$eventBus.$on(
      EventBusConfig.events.BaseProductTileQuickShop.addedToCart,
      this.handleAfterAddSizeToCart
    );
    this.$eventBus.$on(
      EventBusConfig.events.BaseProductTileQuickShop.removedFromCart,
      this.handleAfterRemoveSizeFromCart
    );
  },

  beforeDestroy() {
    this.$eventBus.$off(
      EventBusConfig.events.BaseProductTileQuickShop.addedToCart,
      this.handleAfterAddSizeToCart
    );
    this.$eventBus.$off(
      EventBusConfig.events.BaseProductTileQuickShop.removedFromCart,
      this.handleAfterRemoveSizeFromCart
    );
  },

  methods: {
    closeModal(currentItem) {
      this.shouldShowCartMessages = false;
      if (ITEMS.ADDED_TO_CART === currentItem) this.currentSize = {};
      else this.step = STEPS.INIT;
    },
    handleAddButtonClick() {
      this.initializedFromCart = false; // Reset the flag. State does not comes from cart store now.
      if (this.step === STEPS.INIT) {
        this.nextStep();
        if (this.hasUniqueSelectableSize()) {
          // should directly be added by SFRA, so we'll call to size selector handler
          this.selectSize(this.sizes[0]);
        }
      } else if (this.step === STEPS.SIZE_SELECTION) {
        this.previousStep(); // close size selector
      } else if (this.step === STEPS.ADDED_TO_CART) {
        this.willBeRemovedFromCart = true;
        this.nextStep();
        // Remove original product from cart instead of doubleLook
        const originalProductID = getOriginalPID(this.productId);
        this.removeAllSizesFromCart(originalProductID);
      }
    },
    /**
     * Handle a line from cart
     * @returns {void}
     */
    handleAddedToCart() {
      this.step = STEPS.ADDED_TO_CART;
      this.willBeAddedToCart = false;
    },
    /**
     * Handle event triggered after adding size to cart
     * @param {Object} eventPayload
     * @param {Object} eventPayload.productVariation attributeSelect() response
     * @param {FullProduct} eventPayload.productVariation.product attributeSelect() product
     * @param {Object} eventPayload.productVariation.resources Useful messages
     * @param {Object} eventPayload.cartAddProduct Cart-AddProduct response
     * @param {Cart} eventPayload.cartAddProduct.cart Cart model
     * @param {FullProduct} eventPayload.cartAddProduct.product Variant full product
     * @param {string} eventPayload.cartAddProduct.pliUUID UUID of the added line item
     */
    handleAfterAddSizeToCart(eventPayload) {
      const productId = eventPayload.productVariation?.product?.id;
      const productIdWithColor = getVariationGroupId(productId);

      const shouldHandleEvent = this.product?.id.includes(productIdWithColor);
      if (shouldHandleEvent) {
        // Show only cartMessages when the quickshop have been interacted (clicked)
        this.shouldShowCartMessages = this.willBeAddedToCart;
        // Handle event
        this.handleAddedToCart();
        if (this.showInModal) {
          this.$eventBus.$emit(
            EventBusConfig.commands.modalSizeSelector.setProps,
            {
              currentItem: ITEMS.ADDED_TO_CART,
              selectedSize: this.getLastSelectedSize(),
            }
          );
          // Close modal
          setTimeout(() => {
            this.shouldShowCartMessages = false;
            this.currentSize = {};
          }, 3300);
        }
      }
    },
    /** EventBus quickShop:afterRemoveSizeFromCart event handler
     * @param {string} id - Removed line item ID (variant ID)
     */
    handleAfterRemoveSizeFromCart(id) {
      const pid = getVariationGroupId(id);
      if (this.productId.includes(pid)) {
        // valid for original and DL product
        this.shouldShowCartMessages = this.willBeRemovedFromCart;
        this.step = STEPS.REMOVED_FROM_CART;
      }
    },
    selectSize(size) {
      this.willBeAddedToCart = true;
      this.currentSize = size;
    },
    nextStep() {
      this.step += 1;
    },
    previousStep() {
      this.step -= 1;
    },
    /**
     * Remove from the cart all the line items related to the product (every size)
     * @param {string} variationGroupId ID of the product
     * @returns {void}
     */
    removeAllSizesFromCart(variationGroupId) {
      this.$eventBus.$emit(
        EventBusConfig.commands.cart.removeAllSizesOfProduct,
        variationGroupId
      );
    },
  },
};
</script>
